import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  Download, 
  FileSpreadsheet, 
  FileText, 
  Mail,
  CheckCircle,
  AlertTriangle,
  Settings
} from 'lucide-react';
import { devLog } from '@/utils/logger';
import { format } from 'date-fns';
import { useWorkplaceCurrency } from '@/contexts/WorkplaceCurrencyContext';
import type { PayrollPreviewResult } from '@/services/compensation/types';

interface PayrollExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  payrollData: PayrollPreviewResult[];
  payPeriodStart: Date;
  payPeriodEnd: Date;
}

interface ExportOptions {
  format: 'csv' | 'pdf' | 'xlsx';
  includeDetails: boolean;
  includeSensitive: boolean;
  includeYTD: boolean;
  groupByDepartment: boolean;
  separatePayslips: boolean;
  emailTo?: string;
}

export function PayrollExportDialog({
  open,
  onOpenChange,
  payrollData,
  payPeriodStart,
  payPeriodEnd,
}: PayrollExportDialogProps) {
  const [exportOptions, setExportOptions] = useState<ExportOptions>({
    format: 'csv',
    includeDetails: true,
    includeSensitive: false,
    includeYTD: false,
    groupByDepartment: false,
    separatePayslips: false,
  });
  const [isExporting, setIsExporting] = useState(false);
  const [exportComplete, setExportComplete] = useState(false);
  const { currencyCode } = useWorkplaceCurrency();

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
    }).format(amount);
  };

  const getTotalSummary = () => {
    return payrollData.reduce(
      (acc, result) => ({
        totalEmployees: acc.totalEmployees + 1,
        totalGross: acc.totalGross + result.calculation.totalGrossPay,
        totalNet: acc.totalNet + result.calculation.netPay,
        totalHours: acc.totalHours + (result.timeEntries?.reduce((h, entry) => h + (entry.hours || 0), 0) || 0),
      }),
      { totalEmployees: 0, totalGross: 0, totalNet: 0, totalHours: 0 }
    );
  };

  const generateExportPreview = () => {
    const summary = getTotalSummary();
    
    switch (exportOptions.format) {
      case 'csv':
        return {
          title: 'CSV Spreadsheet Export',
          description: `Comma-separated values file with ${exportOptions.includeDetails ? 'detailed' : 'summary'} payroll data`,
          fileSize: `~${Math.ceil(payrollData.length * (exportOptions.includeDetails ? 2 : 0.5))}KB`,
          columns: exportOptions.includeDetails 
            ? ['Employee Name', 'Employee ID', 'Regular Hours', 'Overtime Hours', 'Gross Pay', 'Deductions', 'Net Pay', 'YTD Gross', 'YTD Net']
            : ['Employee Name', 'Hours', 'Gross Pay', 'Net Pay']
        };
      
      case 'pdf':
        return {
          title: 'PDF Report Export',
          description: exportOptions.separatePayslips 
            ? `Individual payslips for ${summary.totalEmployees} employees`
            : 'Consolidated payroll report with summary and details',
          fileSize: `~${Math.ceil(payrollData.length * (exportOptions.separatePayslips ? 3 : 1))}MB`,
          columns: ['Complete payslip format with company branding']
        };
      
      case 'xlsx':
        return {
          title: 'Excel Workbook Export',
          description: 'Multi-sheet Excel file with summary, details, and analysis tabs',
          fileSize: `~${Math.ceil(payrollData.length * 1.5)}KB`,
          columns: ['Summary Sheet', 'Employee Details', 'Calculations', 'YTD Analysis']
        };
      
      default:
        return null;
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    
    try {
      // Simulate export process
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // In real implementation, this would call the export service
      const exportData = {
        payrollData,
        options: exportOptions,
        period: {
          start: payPeriodStart,
          end: payPeriodEnd
        }
      };

      devLog('Exporting payroll data:', exportData);
      
      // Create mock download
      const filename = `payroll_${format(payPeriodStart, 'yyyy-MM-dd')}_to_${format(payPeriodEnd, 'yyyy-MM-dd')}.${exportOptions.format}`;
      
      // Simulate file download
      const element = document.createElement('a');
      element.href = 'data:text/plain;charset=utf-8,' + encodeURIComponent('Mock payroll export data');
      element.download = filename;
      element.style.display = 'none';
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
      
      setExportComplete(true);
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const updateOption = (key: keyof ExportOptions, value: any) => {
    setExportOptions(prev => ({ ...prev, [key]: value }));
  };

  const summary = getTotalSummary();
  const exportPreview = generateExportPreview();

  if (exportComplete) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              Export Complete
            </DialogTitle>
            <DialogDescription>
              Your payroll export has been generated and downloaded successfully.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Card className="border-green-200 bg-green-50">
              <CardContent className="p-4 text-center">
                <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                <p className="font-medium">Export Successful</p>
                <p className="text-sm text-muted-foreground">
                  File downloaded: payroll_{format(payPeriodStart, 'yyyy-MM-dd')}.{exportOptions.format}
                </p>
              </CardContent>
            </Card>
          </div>
          
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setExportComplete(false);
                onOpenChange(false);
              }}
              className="flex-1"
            >
              Close
            </Button>
            <Button onClick={handleExport} className="flex-1">
              Export Again
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Export Payroll Data
          </DialogTitle>
          <DialogDescription>
            Export payroll calculations for {summary.totalEmployees} employees 
            ({format(payPeriodStart, 'MMM dd')} - {format(payPeriodEnd, 'MMM dd, yyyy')})
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-6">
          {/* Export Options */}
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Export Options</h3>
              
              {/* Format Selection */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Export Format</Label>
                <Select 
                  value={exportOptions.format} 
                  onValueChange={(value: any) => updateOption('format', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="csv">
                      <div className="flex items-center gap-2">
                        <FileSpreadsheet className="h-4 w-4" />
                        CSV - Spreadsheet Format
                      </div>
                    </SelectItem>
                    <SelectItem value="xlsx">
                      <div className="flex items-center gap-2">
                        <FileSpreadsheet className="h-4 w-4" />
                        Excel - Multi-sheet Workbook
                      </div>
                    </SelectItem>
                    <SelectItem value="pdf">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        PDF - Professional Report
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator className="my-4" />

              {/* Content Options */}
              <div className="space-y-4">
                <Label className="text-sm font-medium">Content Options</Label>
                
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="includeDetails"
                      checked={exportOptions.includeDetails}
                      onCheckedChange={(checked) => updateOption('includeDetails', checked)}
                    />
                    <Label htmlFor="includeDetails" className="text-sm">
                      Include detailed breakdown
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="includeSensitive"
                      checked={exportOptions.includeSensitive}
                      onCheckedChange={(checked) => updateOption('includeSensitive', checked)}
                    />
                    <Label htmlFor="includeSensitive" className="text-sm">
                      Include sensitive data (SSN, full pay amounts)
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="includeYTD"
                      checked={exportOptions.includeYTD}
                      onCheckedChange={(checked) => updateOption('includeYTD', checked)}
                    />
                    <Label htmlFor="includeYTD" className="text-sm">
                      Include year-to-date totals
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="groupByDepartment"
                      checked={exportOptions.groupByDepartment}
                      onCheckedChange={(checked) => updateOption('groupByDepartment', checked)}
                    />
                    <Label htmlFor="groupByDepartment" className="text-sm">
                      Group by department
                    </Label>
                  </div>

                  {exportOptions.format === 'pdf' && (
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="separatePayslips"
                        checked={exportOptions.separatePayslips}
                        onCheckedChange={(checked) => updateOption('separatePayslips', checked)}
                      />
                      <Label htmlFor="separatePayslips" className="text-sm">
                        Generate separate payslips
                      </Label>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Preview & Summary */}
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Export Preview</h3>
              
              {/* Summary Stats */}
              <Card className="mb-4">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Payroll Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Employees</p>
                      <p className="font-semibold">{summary.totalEmployees}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Total Hours</p>
                      <p className="font-semibold">{summary.totalHours.toFixed(1)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Gross Pay</p>
                      <p className="font-semibold text-green-600">
                        {formatCurrency(summary.totalGross)}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Net Pay</p>
                      <p className="font-semibold">
                        {formatCurrency(summary.totalNet)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Export Preview */}
              {exportPreview && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">{exportPreview.title}</CardTitle>
                    <p className="text-sm text-muted-foreground">{exportPreview.description}</p>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center text-sm">
                        <span>Estimated file size:</span>
                        <Badge variant="outline">{exportPreview.fileSize}</Badge>
                      </div>
                      
                      <div>
                        <p className="text-sm font-medium mb-2">Data included:</p>
                        <div className="space-y-1">
                          {exportPreview.columns.map((column, index) => (
                            <div key={index} className="flex items-center gap-2 text-xs">
                              <CheckCircle className="h-3 w-3 text-green-500" />
                              <span>{column}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Warnings */}
              {exportOptions.includeSensitive && (
                <Card className="border-yellow-200 bg-yellow-50">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                      <div className="text-sm">
                        <p className="font-medium text-yellow-800">Sensitive Data Warning</p>
                        <p className="text-yellow-700">
                          This export will include sensitive payroll information. 
                          Ensure secure handling and comply with privacy regulations.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>

        <Separator />

        {/* Actions */}
        <div className="flex justify-between items-center">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          
          <Button 
            onClick={handleExport}
            disabled={isExporting || payrollData.length === 0}
            className="min-w-32"
          >
            {isExporting ? (
              <>
                <Settings className="h-4 w-4 mr-2 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Export {exportOptions.format.toUpperCase()}
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}